import React, { useState, useEffect } from "react";
import { colors, font, BRANCHES, PROPOSAL_TYPES, displayName, formatDate } from "../utils/theme.js";
import { STAGES, stageLabel, DEFAULT_NEW_STAGE } from "./stages.js";
import ClientPicker from "../clients/ClientPicker.jsx";

const monoFont = "'JetBrains Mono', monospace";

const EDITABLE_DEFAULTS = {
  client: "", clientId: "", projectManager: "", accountOwner: "", branch: "", proposalType: "", topic: "",
  value: "", submissionDate: "", estimatedResponseDate: "", dueDate: "", notes: "",
};

// Detail panel for viewing/editing one proposal, or creating a new one.
// Writes are delegated to the board via callbacks that resolve to { ok, error }:
//   onSaveFields(fields)  — guarded field edit (existing record)
//   onSetStage(toStage)   — guarded stage change (existing record)
//   onCreate(record)      — create a new proposal
// admins: user objects with role "admin" (Account Owner options).
// clients: client registry [{id, name}] — the Client picker's source (select-only;
// clients are created in Ops Planner, the sole create surface).
export default function DetailPanel({ proposal, users, admins, clients, isNew, presetClientId, onSaveFields, onSetStage, onCreate, onClose }) {
  const [form, setForm] = useState(() => seed(proposal));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    setForm(seed(proposal));
    setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal && proposal.id]);

  // presetClientId (from the client page's add-proposal) locks a new proposal to that client.
  function seed(p) {
    const s = { ...EDITABLE_DEFAULTS, ...(p || {}) };
    if (isNew && presetClientId) s.clientId = presetClientId;
    return s;
  }
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Client is satisfied by a registry link (clientId) or a legacy free-text value on an
  // existing record — never blocked from saving just because it predates the registry.
  const canSave = (form.clientId || form.client) && form.topic && form.projectManager && form.branch;

  const handleSave = async () => {
    if (!canSave || busy) return;
    setBusy(true); setMsg(null);
    if (isNew) {
      const res = await onCreate({ ...pickEditable(form), stage: form.stage || DEFAULT_NEW_STAGE });
      setBusy(false);
      if (res && res.ok) onClose();
      else setMsg({ type: "error", text: (res && res.error) || "Could not create proposal." });
      return;
    }
    const res = await onSaveFields(pickEditable(form));
    setBusy(false);
    if (res && res.ok) setMsg({ type: "ok", text: "Saved." });
    else setMsg({ type: "error", text: (res && res.error) || "Save failed." });
  };

  const handleStage = async (toStage) => {
    if (busy || toStage === proposal.stage) return;
    setBusy(true); setMsg(null);
    const res = await onSetStage(toStage);
    setBusy(false);
    if (!res || !res.ok) setMsg({ type: "error", text: (res && res.error) || "Stage change failed." });
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 6, color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 6 };
  const field = { marginBottom: 16 };
  const history = Array.isArray(proposal && proposal.stageHistory) ? proposal.stageHistory : [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ width: 680, maxHeight: "88vh", overflow: "auto", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 32 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.text }}>{isNew ? "New Proposal" : "Edit Proposal"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: colors.textDim, fontSize: 24, cursor: "pointer" }} aria-label="Close">×</button>
        </div>

        {!isNew && (
          <div style={{ ...field, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
            <label style={labelStyle} htmlFor="stage-select">Stage</label>
            <select id="stage-select" style={{ ...inputStyle, cursor: busy ? "wait" : "pointer" }} value={proposal.stage || ""} disabled={busy}
              onChange={(e) => handleStage(e.target.value)}>
              {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 6 }}>Change here or drag the card. Any stage is allowed; every move is logged below.</div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {/* Client — registry picker (select-only; new clients are created in Ops Planner) */}
          <div style={field}>
            <label style={labelStyle}>Client *</label>
            <ClientPicker
              value={form.clientId || ""}
              clients={clients || []}
              allowCreate={false}
              onChange={(id) => setForm((f) => ({ ...f, clientId: id, client: "" }))}
              legacyName={!form.clientId ? form.client : undefined}
            />
          </div>

          <div style={field}>
            <label style={labelStyle}>Project Manager *</label>
            <select style={inputStyle} value={form.projectManager} onChange={(e) => update("projectManager", e.target.value)}>
              <option value="">Select PM</option>
              {(users || []).map((u) => <option key={u.id} value={displayName(u)}>{displayName(u)}</option>)}
            </select>
          </div>

          <div style={field}>
            <label style={labelStyle}>Account Owner</label>
            <select style={inputStyle} value={form.accountOwner || ""} onChange={(e) => update("accountOwner", e.target.value)}>
              <option value="">Unassigned</option>
              {form.accountOwner && !(admins || []).some((u) => displayName(u) === form.accountOwner) && (
                <option value={form.accountOwner}>{form.accountOwner}</option>
              )}
              {(admins || []).map((u) => <option key={u.id} value={displayName(u)}>{displayName(u)}</option>)}
            </select>
            <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>Admins only.</div>
          </div>

          <div style={field}>
            <label style={labelStyle}>Agency / Branch *</label>
            <select style={inputStyle} value={form.branch} onChange={(e) => update("branch", e.target.value)}>
              <option value="">Select branch</option>
              {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div style={field}>
            <label style={labelStyle}>Proposal Type</label>
            <select style={inputStyle} value={form.proposalType} onChange={(e) => update("proposalType", e.target.value)}>
              <option value="">Select type</option>
              {PROPOSAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={field}><label style={labelStyle}>Value ($)</label><input style={inputStyle} type="number" value={form.value} onChange={(e) => update("value", e.target.value)} placeholder="Contract value" /></div>

          <div style={{ ...field, gridColumn: "1 / -1" }}><label style={labelStyle}>Topic / Title *</label><input style={inputStyle} value={form.topic} onChange={(e) => update("topic", e.target.value)} placeholder="Proposal topic or title" /></div>

          <div style={field}><label style={labelStyle}>Due Date</label><input style={inputStyle} type="date" value={form.dueDate || ""} onChange={(e) => update("dueDate", e.target.value)} /><div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>Solicitation deadline — drives the card urgency flag.</div></div>
          <div style={field}><label style={labelStyle}>Submission Date</label><input style={inputStyle} type="date" value={form.submissionDate || ""} onChange={(e) => update("submissionDate", e.target.value)} /></div>
          <div style={field}><label style={labelStyle}>Est. Response Date</label><input style={inputStyle} type="date" value={form.estimatedResponseDate || ""} onChange={(e) => update("estimatedResponseDate", e.target.value)} /></div>
          <div style={{ ...field, gridColumn: "1 / -1" }}><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Additional notes..." /></div>
        </div>

        {!isNew && (
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Stage History</div>
            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: colors.textDim }}>No history recorded.</div>
            ) : (
              <div style={{ borderLeft: `2px solid ${colors.border}`, paddingLeft: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                {[...history].slice().reverse().map((h, i) => (
                  <div key={i} style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: -19, top: 4, width: 8, height: 8, borderRadius: "50%", background: i === 0 ? colors.accent : colors.textDim }} />
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{stageLabel(h.stage)}{h.override ? <span style={{ fontSize: 10, color: colors.warning, marginLeft: 6 }}>OVERRIDE</span> : null}</div>
                    <div style={{ fontSize: 11, color: colors.textDim, fontFamily: monoFont }}>{formatDate((h.enteredAt || "").slice(0, 10))} · {h.changedBy || "system"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {msg && <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: msg.type === "error" ? colors.danger : colors.success }}>{msg.text}</div>}

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${colors.borderLight}`, borderRadius: 8, color: colors.textMuted, fontSize: 14, cursor: "pointer", fontFamily: font }}>Close</button>
          <button onClick={handleSave} disabled={!canSave || busy}
            style={{ padding: "10px 24px", background: colors.accent, border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 600, cursor: (canSave && !busy) ? "pointer" : "not-allowed", fontFamily: font, opacity: (canSave && !busy) ? 1 : 0.4 }}>
            {busy ? "Saving..." : isNew ? "Create Proposal" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function pickEditable(form) {
  const out = {};
  for (const k of Object.keys(EDITABLE_DEFAULTS)) out[k] = form[k];
  return out;
}
